/**
 * SOI Agent API client - handles SOI file uploads and extraction status polling.
 *
 * Upload Flow:
 * 1. getPresignedUrlsBatch() - Get S3 presigned URLs for direct upload
 * 2. uploadToS3() - Upload files directly to S3 (bypasses API size limits)
 * 3. SQS triggers processing automatically (no need to call startProcessing)
 * 4. Poll status via getUploadStatus() or use useUploadDetailQuery hook
 */
import { MaggieHttp } from '../MaggieHttp';
import { EXTENSION_TO_CONTENT_TYPE, FileExtension } from '../../constants/file-upload.constants';

/**
 * Get content type from filename extension.
 * Must match backend logic to ensure presigned URL content-type matches upload.
 * Backend logic: api/src/file-upload/file-upload.service.ts - getContentType()
 */
export function getContentTypeFromFilename(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop() as FileExtension | undefined;
  return (ext && EXTENSION_TO_CONTENT_TYPE[ext]) || 'application/octet-stream';
}

// Step types matching backend SoiStepType enum
export type SoiStepType =
  | 'input_received'
  | 'llm_reasoning'
  | 'code_generated'
  | 'code_execution'
  | 'llm_interpretation'
  | 'final_output';

export interface SoiStep {
  timestamp: string;
  stepType: SoiStepType;
  content: string | Record<string, unknown>;
  durationMs?: number;
  status: 'success' | 'error';
}

export type SoiUploadStatus = 'pending' | 'processing' | 'done' | 'failed' | 'awaiting_alias_confirmation';

// ARGUS unmatched-name confirmation flow (FD-2642)
export type AliasConfidence = 'high' | 'medium' | 'low';

export interface AliasSuggestion {
  id: number;
  name: string;
  confidence: AliasConfidence;
  rationale: string;
}

export interface UnmatchedFundEntry {
  externalName: string;
  context?: string;
  suggestions: AliasSuggestion[];
}

export interface UnmatchedCompanyEntry {
  externalName: string;
  context?: string;
  suggestions: AliasSuggestion[];
}

/** A SOI company name the client's Company Mappings resolve (read-only in the UI). */
export interface CompanyMappingResolvedEntry {
  externalName: string;
  companyId: number;
  companyName: string;
}

export interface UnmatchedResponse {
  funds: UnmatchedFundEntry[];
  companies: UnmatchedCompanyEntry[];
  companyMappingResolved?: CompanyMappingResolvedEntry[];
}

export async function getUnmatched(uploadId: number): Promise<UnmatchedResponse> {
  const { data } = await MaggieHttp.maggie.get<UnmatchedResponse>(`/argus/upload/${uploadId}/unmatched`);
  return data;
}

export type AliasUpsertSource = 'manual' | 'llm_auto';

export interface UpsertFundAliasRequest {
  externalName: string;
  fundId: number;
  source?: AliasUpsertSource;
}

export interface UpsertCompanyAliasRequest {
  externalName: string;
  companyId: number;
  source?: AliasUpsertSource;
}

export async function upsertFundAlias(payload: UpsertFundAliasRequest): Promise<void> {
  await MaggieHttp.maggie.post('/argus/alias/fund', payload);
}

export async function upsertCompanyAlias(payload: UpsertCompanyAliasRequest): Promise<void> {
  await MaggieHttp.maggie.post('/argus/alias/company', payload);
}

// FD-2449 — user-managed company/fund alias list. `source` records where a
// mapping came from (human vs agent/LLM); every value is a live alias, not an
// approval state. compareSOI caller↔SOI bridge rows are excluded server-side.
export type SoiEntityAliasSource =
  'manual' | 'agent' | 'poc' | 'llm_auto' | 'value_attribution' | 'llm_caller_soi';

export interface SoiCompanyAlias {
  id: number;
  externalName: string;
  companyId: number | null;
  companyName: string | null;
  source: SoiEntityAliasSource;
  createdAt: string;
  updatedAt: string;
}

export interface SoiFundAlias {
  id: number;
  externalName: string;
  fundId: number | null;
  fundName: string | null;
  source: SoiEntityAliasSource;
  createdAt: string;
  updatedAt: string;
}

export interface ArgusAliasesResponse {
  funds: SoiFundAlias[];
  companies: SoiCompanyAlias[];
}

export async function getArgusAliases(): Promise<ArgusAliasesResponse> {
  const { data } = await MaggieHttp.maggie.get<ArgusAliasesResponse>('/argus/alias');
  return data;
}

export async function deleteArgusCompanyAlias(id: number): Promise<void> {
  await MaggieHttp.maggie.delete(`/argus/alias/company/${id}`);
}

export async function deleteArgusFundAlias(id: number): Promise<void> {
  await MaggieHttp.maggie.delete(`/argus/alias/fund/${id}`);
}

/** Clear an alias's target but keep the row, so it stays visible as unmapped and re-mappable. */
export async function unmapArgusCompanyAlias(id: number): Promise<void> {
  await MaggieHttp.maggie.post(`/argus/alias/company/${id}/unmap`);
}

export async function unmapArgusFundAlias(id: number): Promise<void> {
  await MaggieHttp.maggie.post(`/argus/alias/fund/${id}/unmap`);
}

export async function resumeArgusUpload(uploadId: number): Promise<SoiAgentUploadDetail> {
  const { data } = await MaggieHttp.maggie.post<never, SoiAgentUploadDetail>(
    `/argus/upload/${uploadId}/resume`
  );
  return data;
}

export async function skipUnmatched(uploadId: number): Promise<SoiAgentUploadDetail> {
  const { data } = await MaggieHttp.maggie.post<never, SoiAgentUploadDetail>(
    `/argus/upload/${uploadId}/skip-unmatched`
  );
  return data;
}

// ARGUS field alias (FD-2922 Phase 2) — maps an external SOI column header to a
// canonical foresight recon-config field. `source` flags whether the mapping
// was learned automatically by the LLM or asserted by a human; manual entries
// are returned with `locked: true` so they survive subsequent auto-learns.
export type SoiAliasSource = 'manual' | 'llm_auto';

export interface SoiFieldAlias {
  id: number;
  externalName: string;
  field: string;
  source: SoiAliasSource;
  locked: boolean;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function getArgusFieldAliases(): Promise<SoiFieldAlias[]> {
  const { data } = await MaggieHttp.maggie.get<SoiFieldAlias[]>('/argus/field-alias');
  return data;
}

export interface UpsertFieldAliasRequest {
  externalName: string;
  field: string;
}

export async function upsertArgusFieldAlias(payload: UpsertFieldAliasRequest): Promise<SoiFieldAlias> {
  const { data } = await MaggieHttp.maggie.put<UpsertFieldAliasRequest, SoiFieldAlias>(
    '/argus/field-alias',
    payload
  );
  return data;
}

export async function deleteArgusFieldAlias(id: number): Promise<void> {
  await MaggieHttp.maggie.delete(`/argus/field-alias/${id}`);
}

/**
 * Per-row reconciliation values for one upload. The recon table reads from
 * here (canonical source) rather than `results.securities` so that every recon
 * field configured in `reconConfig` (realized G/L, unrealized G/L, total cost,
 * etc.) shows up automatically without per-client FE code.
 */
export interface ReconValueRow {
  companyId: number | null;
  companyName: string;
  fundId: number | null;
  fundName: string;
  reconConfigId: number;
  field: string | null;
  fieldDisplayName: string | null;
  soiValue: string | null;
  soiCompanyName: string | null;
  foresightValue: string | null;
  status: string;
  hidden: boolean;
  asOfDate: string | null;
  uploadId: number;
}

export async function getReconValues(uploadId: number): Promise<ReconValueRow[]> {
  const { data } = await MaggieHttp.maggie.get<ReconValueRow[]>(`/recon-value`, {
    params: { uploadId: String(uploadId) },
  });
  return data;
}

// Presign endpoint types
export interface PresignRequest {
  fileName: string;
  period: string;
  fundName?: string; // Optional - defaults to filename on backend
}

// Internal type for backend DTO
interface BackendPresignFile {
  fileName: string;
  type: string;
  contentType: string;
  period?: string;
}

export interface PresignResponse {
  success: boolean;
  uploadId: number;
  s3Key: string;
  signedUrl: string;
  expiresIn: number;
}

export interface PresignBatchRequest {
  files: PresignRequest[];
}

export interface PresignBatchResponse {
  success: boolean;
  uploads: Array<{
    uploadId: number;
    s3Key: string;
    signedUrl: string;
    expiresIn: number;
  }>;
}

// Upload status types
export interface SoiAgentUploadDetail {
  id: number;
  fileName: string;
  fundName: string;
  period: string;
  status: SoiUploadStatus;
  source: string | null;
  errorMessage: string | null;
  // backend: surface on upload detail
  profileId?: string;
  agentInput: {
    companyMappingsCount?: number;
    fileIndex?: number;
    totalFiles?: number;
  } | null;
  steps: SoiStep[];
  results: {
    fundName?: string;
    fundPeriod?: string;
    extracted?: Array<{
      companyName: string;
      securityName?: string;
      fundName?: string;
      // Agent-resolved fund mapping per row. `fundName` is the raw SOI string,
      // `matchedFundName`/`matchedFundId` are the foresight-side resolution
      // (null when no alias matches). Surfaced as the Fund column on the
      // Reformatting Check tab so multi-fund extractions are legible.
      matchedFundId?: number | null;
      matchedFundName?: string | null;
      shares: number;
      cost: number;
      fmv: number;
      // All other fieldKeys the agent emitted (totalCost, realizedValue,
      // unrealizedGL, realizedCost, investmentAmount, unrealizedValue,
      // realizedGL, urgl, rgl, etc.). Per-row keys vary by SOI file shape.
      [extraField: string]: number | string | null | undefined;
    }>;
    securities?: Array<{
      companyName: string;
      securityName?: string;
      fundName?: string;
      shares: number;
      cost: number;
      fmv: number;
      foresightCost?: number;
      foresightUnrealizedValue?: number; // legacy soi-agent path emits this name; same value as foresightCost
      foresightShares?: number;
      foresightFmv?: number;
      // When N SOI lots map to one investran target (Phase 3 grouping), every
      // member carries the same groupKey (= investran position name). Members
      // sharing this value should render visually grouped in the FE.
      foresightGroupKey?: string | null;
      // Agent-resolved fund/company mapping per row. `companyName`/`fundName`
      // are the raw SOI-extracted strings; `matched*Name`/`matched*Id` are the
      // foresight-side resolution. Both are surfaced as paired columns in the
      // recon grid so admins can override the mapping inline.
      matchedFundId?: number | null;
      matchedFundName?: string | null;
      matchedCompanyId?: number | null;
      matchedCompanyName?: string | null;
    }>;
    // Company-level reconValue rows (no per-security detail). Populated for
    // every upload from the reconValue table — for files whose securities[]
    // is empty (company-level uploads like altamont), this is the only path
    // the FE has to render the recon grid. Rows that would duplicate
    // security-aligned subtotals from securities[] are filtered out on the
    // backend (see SoiAgentService.getCompanyLevelReconValues).
    reconValues?: Array<{
      companyId: number | null;
      companyName: string | null;
      fundId: number | null;
      fundName: string | null;
      field: string | null;
      displayName: string | null;
      soiValue: string | null;
      foresightValue: string | null;
      status: string | null;
      asOfDate: string | null;
      reconConfigId: number | null;
      // Literal SOI column header text that produced this value, when the
      // parser was able to attribute the row to a single source column.
      // Surfaces in the "SOI Field" column of the recon grid and is the key
      // used to upsert a SoiFieldAlias.
      soiColumn?: string | null;
      // The user's actual workbook column header (two-phase manifest
      // extraction), e.g. "Cash Proceeds" where soiColumn is the normalized
      // key "distributions". Null on single-phase/legacy uploads.
      soiHeader?: string | null;
      // Per-security recon row carries the SOI security label; null on
      // company-level rollup rows (sum across securities for the same field).
      // FD-3206: grouped recon grid uses this to place per-security pair rows
      // under the right security node.
      securityName?: string | null;
      // FD-4167: per-tranche/round discriminator (the position's investment/
      // issuance date). Distinguishes two positions that share a securityName
      // so the grid renders them as separate security nodes instead of
      // re-merging by name. Null on rollup rows / legacy uploads.
      tranche?: string | null;
      // reconConfig.type ("currency" / "number" / "percent") — drives the
      // grouped recon grid's value formatter selection.
      type?: string | null;
      // Grouping-difference pairing. When the client split one
      // Foresight position into several SOI lines, `groupId` links the matched
      // parent security to the SOI-only members that fill its residual;
      // `groupRole` marks which is which; `groupParentSecurityName` /
      // `groupParentTranche` name the parent so the grid nests the members
      // under it and subtotals the addition. Null outside any group.
      groupId?: string | null;
      groupRole?: 'parent' | 'member' | null;
      groupParentSecurityName?: string | null;
      groupParentTranche?: string | null;
    }>;
    summary?: {
      extractedCount?: number;
      totalCost?: number;
      totalFmv?: number;
      matchedCount?: number;
      unmatchedCount?: number;
    };
    // Origin SOI file name -> the fund ids that file reconciled to. Present on
    // multi-file uploads; lets the Files row scope the recon grid to a single
    // selected file's funds. Absent on older uploads (grid falls back to the
    // combined, all-funds view).
    fileFunds?: Record<string, number[]>;
  } | null;
  createdAt: string;
  finishedAt: string | null;
}

/**
 * Convert frontend request to backend DTO format
 */
function toBackendFormat(request: PresignRequest): BackendPresignFile {
  return {
    fileName: request.fileName,
    type: 'soi-recon',
    contentType: getContentTypeFromFilename(request.fileName),
    period: request.period,
  };
}

/**
 * Get presigned URLs for batch file upload.
 * Returns signed S3 URLs that allow direct browser-to-S3 uploads.
 */
export async function getPresignedUrlsBatch(files: PresignRequest[]): Promise<PresignBatchResponse> {
  const { data } = await MaggieHttp.maggie.post<{ files: BackendPresignFile[] }, PresignBatchResponse>(
    '/file-upload/presign',
    { files: files.map(toBackendFormat) }
  );
  return data;
}

/**
 * Upload file directly to S3 using presigned URL.
 * Note: Requires S3 bucket CORS configuration.
 */
export async function uploadToS3(signedUrl: string, file: File): Promise<void> {
  // Must use same content type logic as backend to match presigned URL
  const contentType = getContentTypeFromFilename(file.name);

  const response = await fetch(signedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': contentType,
    },
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => 'Unable to read error body');
    console.error('S3 upload error details:', { status: response.status, body: errorBody });
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
  }
}

/**
 * Get status and progress for a single upload.
 */
export async function getUploadStatus(uploadId: number): Promise<SoiAgentUploadDetail> {
  const { data } = await MaggieHttp.maggie.get<SoiAgentUploadDetail>(`/soi-agent/upload/${uploadId}`);
  return data;
}

// List response types

// One file within a submission. A multi-file upload bundles several of these
// under a single upload record.
export interface SoiUploadListFile {
  fileName: string;
  // Per-file extraction status (the API's SoiFileStatus). Note `extracting`,
  // which the record-level SoiUploadStatus spells `processing`.
  status: 'pending' | 'extracting' | 'done' | 'failed';
  kind?: string;
  errorMessage?: string | null;
  extractedAt?: string | null;
}

export interface SoiUploadListItem {
  id: number;
  fileName: string;
  fundName: string;
  period: string;
  status: SoiUploadStatus;
  source: string | null;
  errorMessage: string | null;
  stepCount: number;
  latestStep: SoiStep | null;
  createdAt: string;
  finishedAt: string | null;
  createdBy: string | null;
  unmatchedCount: number;
  // Files bundled into this submission. Single-file uploads have one entry;
  // multi-file uploads list each file so the UI can surface them individually.
  files?: SoiUploadListFile[];
}

export interface SoiUploadsResponse {
  uploads: SoiUploadListItem[];
}

export interface SoiUploadCounts {
  total: number;
  done: number;
  failed: number;
  pending: number;
  processing: number;
}

/**
 * Get list of recent uploads with status summary.
 */
export async function getUploads(fundName?: string, limit = 100): Promise<SoiUploadsResponse> {
  const params = new URLSearchParams();
  if (fundName) {
    params.set('fundName', fundName);
  }
  params.set('limit', String(limit));

  const { data } = await MaggieHttp.maggie.get<SoiUploadsResponse>(`/soi-agent/uploads?${params.toString()}`);
  return data;
}

/**
 * Get upload counts by status.
 */
export async function getUploadCounts(): Promise<SoiUploadCounts> {
  const { data } = await MaggieHttp.maggie.get<SoiUploadCounts>('/soi-agent/uploads/counts');
  return data;
}

/**
 * Get human-readable label for step type.
 */
export function getStepLabel(stepType: SoiStepType, fileIndex?: number, totalFiles?: number): string {
  const fileInfo = totalFiles && totalFiles > 1 ? ` (file ${fileIndex} of ${totalFiles})` : '';

  const labels: Record<SoiStepType, string> = {
    input_received: `[input_received] File received${fileInfo}...`,
    llm_reasoning: `[llm_reasoning] Preparing context${fileInfo}...`,
    code_generated: `[code_generated] AI agent processing${fileInfo}...`,
    code_execution: `[code_execution] Analyzing spreadsheet${fileInfo}...`,
    llm_interpretation: `[llm_interpretation] Parsing results${fileInfo}...`,
    final_output: `[final_output] Complete${fileInfo}!`,
  };
  return labels[stepType] || stepType;
}

/**
 * Check if processing has reached a terminal state.
 */
export function isProcessingComplete(status: SoiUploadStatus): boolean {
  return status === 'done' || status === 'failed';
}

// Rerun response type. ARGUS rerun returns { newUploadId, originalUploadId,
// accepted: true }; legacy soi-agent rerun returns { newUploadId, originalUploadId,
// success, status }. We normalize on the two id fields that both shapes carry.
export interface RerunResponse {
  uploadId: number;
  rowsWritten: number;
}

/**
 * Refresh Foresight values + recon statuses for a done upload IN PLACE. No
 * agent re-extraction (SOI side doesn't change), no new soi_upload row —
 * just re-queries the foresight side against the current alias state
 * (argusFieldAlias / soiFundAlias / soiCompanyAlias) and rewrites
 * soi_upload.results.reconValues on the same uploadId. Used by both the
 * full rerun button and inline column/fund/company remap saves.
 */
export async function rerunUpload(uploadId: number): Promise<RerunResponse> {
  const { data } = await MaggieHttp.maggie.post<never, RerunResponse>(
    `/argus/extraction/${uploadId}/refresh`
  );
  return data;
}

/**
 * Targeted single-mapping refresh. Only re-resolves the rows affected by ONE
 * alias edit (column / fund / company) — typically ~100ms. Used by the
 * inline mapping editor in the recon monitor; the full-rerun button still
 * uses `rerunUpload`.
 */
export type MappingEdit =
  | { kind: 'field'; externalName: string; field: string }
  | { kind: 'unset'; externalName: string }
  | { kind: 'fund'; externalName: string; fundId: number }
  | { kind: 'company'; externalName: string; companyId: number };

export interface RefreshMappingResponse {
  uploadId: number;
  rowsUpdated: number;
}

export async function refreshMapping(uploadId: number, edit: MappingEdit): Promise<RefreshMappingResponse> {
  const { data } = await MaggieHttp.maggie.post<MappingEdit, RefreshMappingResponse>(
    `/argus/extraction/${uploadId}/refresh-mapping`,
    edit
  );
  return data;
}
